import { Stack, Switch, Text, Paper, Divider } from "@mantine/core";
import { useUserPreferencesStore } from "@/utils/stores/userPreferencesStore";
import { useEffect, useState } from "react";
import { notifications } from "@mantine/notifications";
import SettingsHeader from "../SettingsHeader/SettingsHeader";

interface UserPreferencesSectionProps {
  onPreferencesChange?: () => void;
}

/**
 * Component for managing user preferences related to One-Click export functionality.
 * Displays toggle switches for various export automation preferences.
 */
export default function UserPreferencesSection({
  onPreferencesChange,
}: UserPreferencesSectionProps) {
  const { settings, loading, loadSettings, updateSetting } =
    useUserPreferencesStore();
  const [updatingFields, setUpdatingFields] = useState<Set<string>>(new Set());

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleToggleChange = async (
    key: keyof typeof settings,
    value: boolean
  ) => {
    setUpdatingFields((prev) => new Set(prev).add(key));
    try {
      await updateSetting(key, value);
      onPreferencesChange?.();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to update preference. Please try again.",
        color: "red",
      });
    } finally {
      setUpdatingFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  if (loading && !settings) {
    return (
      <Paper p="md">
        <Text>Loading preferences...</Text>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      <SettingsHeader
        title="One-Click Export Preferences"
        order={4}
        description="Customize how One-Click Return auto-fills your tax software."
      />

      <Stack gap="sm">
        <Switch
          label="Auto-select 'No State Tax' for applicable states"
          description="Automatically check the box for states with no state income tax (e.g., FL, TX, WA)"
          checked={settings.exportAutoSelectNoStateTax}
          onChange={(event) =>
            handleToggleChange(
              "exportAutoSelectNoStateTax",
              event.currentTarget.checked
            )
          }
          disabled={updatingFields.has("exportAutoSelectNoStateTax")}
        />

        <Divider />

        <Switch
          label="Auto-answer due diligence questions"
          description="Automatically complete due diligence questionnaires required by the IRS"
          checked={settings.exportAnswerDueDiligence}
          onChange={(event) =>
            handleToggleChange(
              "exportAnswerDueDiligence",
              event.currentTarget.checked
            )
          }
          disabled={updatingFields.has("exportAnswerDueDiligence")}
        />

        <Divider />

        <Switch
          label="Auto-complete third party designator"
          description="Automatically fill third party designator section on state tax forms"
          checked={settings.exportAnswerThirdPartyDesignator}
          onChange={(event) =>
            handleToggleChange(
              "exportAnswerThirdPartyDesignator",
              event.currentTarget.checked
            )
          }
          disabled={updatingFields.has("exportAnswerThirdPartyDesignator")}
        />

        <Divider />

        <Switch
          label="Use SSN last 4 digits as default client PIN"
          description="Use the last 4 digits of the client's SSN as the default signature PIN"
          checked={settings.useSsnLast4AsClientPin}
          onChange={(event) =>
            handleToggleChange(
              "useSsnLast4AsClientPin",
              event.currentTarget.checked
            )
          }
          disabled={updatingFields.has("useSsnLast4AsClientPin")}
        />
      </Stack>
    </Stack>
  );
}
